/*
  li-mobile.js (Lucky Inger, 2026-10-20)

  Phone behaviour shared by every order page, the cart and the artwork
  request form. Loaded once from layout/theme.liquid, deferred. Pairs with
  assets/li-mobile.css. Touches no section file and no prices:

  1. Number pad: quantity boxes get inputmode="numeric", a Done key, and
     select their contents on tap so typing replaces the old number.
  2. Hold to count: holding any + or - stepper repeats it, speeding up the
     longer it is held. It clicks the section's own button, so the
     section's maths, caps and limits stay in charge.
  3. Keyboard up: fixed add-to-order bars step aside while a field is being
     typed in (class li-kb-open on <html>), so they never cover the field.
  4. Swipe photos: a sideways swipe on a preview image moves to the next or
     previous thumbnail, or the next print location tab (DTF, screen print).

  Desktop (and phones where it applies):
  5. Spreadsheet paste: pasting a row of numbers ("4  8  10  6" from Excel
     or Sheets) into a size box fills that box and the ones after it.
  6. Enter moves to the next size box on desktop, like a spreadsheet.
     Shift + Up / Down changes a size box by 10.
  7. Scrolling the page with the mouse over a focused number box no longer
     changes its value by accident.

  Everything is delegated from document, so sections redrawn by cart.js
  keep working. A MutationObserver re-applies the field attributes to
  fields drawn later.
*/
(function () {
  'use strict';
  if (window.__liMobile) return;
  window.__liMobile = true;

  var STEPPER = '[class$="-stepper-btn"], [class*="-stepper-btn "], [data-dtf-act="adj"], .ar-step-btn';
  var QTY_FIELD = 'input[type="number"], [class$="-stepper-input"], [class*="-stepper-input "], [class$="-qty-input"], [class*="-qty-input "]';
  var BAR = '[class$="-sticky-qty"], [class*="-sticky-qty "], [class$="-sticky-bar"], [class*="-sticky-bar "], .ar-sticky-bar';

  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(sel)) return el;
      el = el.parentElement;
    }
    return null;
  }

  /* 1. Number pad ------------------------------------------------------ */
  function tuneField(el) {
    if (!el || el.__liTuned || el.type === 'hidden' || el.type === 'range') return;
    el.__liTuned = true;
    if (!el.getAttribute('inputmode')) el.setAttribute('inputmode', 'numeric');
    if (!el.getAttribute('enterkeyhint')) el.setAttribute('enterkeyhint', 'done');
    if (!el.getAttribute('autocomplete')) el.setAttribute('autocomplete', 'off');
  }
  function tuneAll(root) {
    var list = (root || document).querySelectorAll ? (root || document).querySelectorAll(QTY_FIELD) : [];
    Array.prototype.forEach.call(list, tuneField);
  }

  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (t.matches(QTY_FIELD)) {
      tuneField(t);
      // Select the number so typing replaces it (after iOS places the caret)
      setTimeout(function () { try { t.select(); } catch (x) {} }, 0);
    }
    // 3. keyboard up: hide fixed bars unless the field lives inside one
    if (t.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="range"]), textarea, select') && !closest(t, BAR)) {
      document.documentElement.classList.add('li-kb-open');
    }
  });
  document.addEventListener('focusout', function () {
    setTimeout(function () {
      var a = document.activeElement;
      if (!a || !a.matches || !a.matches('input, textarea, select')) {
        document.documentElement.classList.remove('li-kb-open');
      }
    }, 60);
  });
  var SIZE_FIELD = '[class$="-stepper-input"], [class*="-stepper-input "], [data-dtf-qty]';
  var finePointer = window.matchMedia ? window.matchMedia('(pointer: fine)') : { matches: false };

  function setValue(el, v) {
    el.value = v;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }
  // The size boxes after this one on the same page section, in reading order
  function sizeBoxesFrom(el) {
    var scope = closest(el, '.shopify-section') || closest(el, 'section') || document;
    var all = Array.prototype.filter.call(scope.querySelectorAll(SIZE_FIELD), function (x) {
      return x.offsetParent !== null && !x.disabled && !x.readOnly;
    });
    var i = all.indexOf(el);
    return i < 0 ? [el] : all.slice(i);
  }

  // 6. Enter: next size box on desktop; on a phone, Done closes the keyboard
  document.addEventListener('keydown', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches(QTY_FIELD)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      var nextBox = finePointer.matches && t.matches(SIZE_FIELD) ? sizeBoxesFrom(t)[1] : null;
      if (nextBox) nextBox.focus(); else t.blur();
      return;
    }
    if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && t.matches(SIZE_FIELD)) {
      e.preventDefault();
      var cur = parseInt(t.value, 10) || 0;
      setValue(t, Math.max(0, cur + (e.key === 'ArrowUp' ? 10 : -10)));
    }
  });

  // 5. Spreadsheet paste across size boxes
  document.addEventListener('paste', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches(SIZE_FIELD)) return;
    var text = (e.clipboardData || window.clipboardData);
    text = text ? text.getData('text') : '';
    var parts = String(text).trim().split(/[\t,;\r\n ]+/).filter(function (x) { return x !== ''; });
    if (parts.length < 2 || !parts.every(function (x) { return /^\d{1,4}$/.test(x); })) return;
    e.preventDefault();
    var boxes = sizeBoxesFrom(t);
    parts.slice(0, boxes.length).forEach(function (n, i) { setValue(boxes[i], parseInt(n, 10)); });
    var last = boxes[Math.min(parts.length, boxes.length) - 1];
    if (last) { last.focus(); try { last.select(); } catch (x) {} }
  });

  // 7. Mouse wheel over a focused number box scrolls the page, not the number
  document.addEventListener('wheel', function (e) {
    var t = e.target;
    if (t && t.type === 'number' && document.activeElement === t) t.blur();
  }, { passive: true });

  /* 2. Hold to count --------------------------------------------------- */
  var hold = null;

  // A stepper redrawn mid-hold is found again by its own data attributes.
  function twinSelector(el) {
    var sel = el.tagName.toLowerCase();
    Array.prototype.forEach.call(el.attributes, function (a) {
      if (a.name.indexOf('data-') === 0 || a.name === 'aria-label') {
        sel += '[' + a.name + '="' + String(a.value).replace(/"/g, '\\"') + '"]';
      }
    });
    return sel;
  }
  function liveTwin(h) {
    if (h.el.isConnected) return h.el;
    try { var n = document.querySelector(h.sel); if (n) { h.el = n; return n; } } catch (x) {}
    return null;
  }
  function stopHold() {
    if (!hold) return;
    clearTimeout(hold.t);
    if (hold.el) hold.el.classList.remove('li-holding');
    hold = null;
  }
  function tick() {
    if (!hold) return;
    var el = liveTwin(hold);
    if (!el || el.disabled) { stopHold(); return; }
    hold.count++;
    hold.fired = true;
    el.click();
    var gap = hold.count > 25 ? 35 : hold.count > 10 ? 60 : 100;
    hold.t = setTimeout(tick, gap);
  }

  document.addEventListener('pointerdown', function (e) {
    if (e.button && e.button !== 0) return;
    var btn = closest(e.target, STEPPER);
    if (!btn || btn.disabled) return;
    stopHold();
    hold = { el: btn, sel: twinSelector(btn), count: 0, fired: false, t: 0 };
    btn.classList.add('li-holding');
    hold.t = setTimeout(tick, 420);
  }, { passive: true });
  window.addEventListener('blur', function (e) { if (e.target === window) stopHold(); });
  ['pointerup', 'pointercancel'].forEach(function (ev) {
    window.addEventListener(ev, function () {
      // A hold that already counted swallows the click the release would add
      if (hold && hold.fired) {
        var swallow = function (ce) { ce.stopPropagation(); ce.preventDefault(); document.removeEventListener('click', swallow, true); };
        document.addEventListener('click', swallow, true);
        setTimeout(function () { document.removeEventListener('click', swallow, true); }, 350);
      }
      stopHold();
    }, true);
  });
  document.addEventListener('pointermove', function (e) {
    if (!hold || !hold.el.isConnected) return;
    var r = hold.el.getBoundingClientRect();
    if (e.clientX < r.left - 20 || e.clientX > r.right + 20 || e.clientY < r.top - 20 || e.clientY > r.bottom + 20) stopHold();
  }, { passive: true });
  // Long press must not open the copy / select menu on a stepper
  document.addEventListener('contextmenu', function (e) {
    if (closest(e.target, STEPPER)) e.preventDefault();
  });

  /* 4. Swipe photos ---------------------------------------------------- */
  var swipe = null;
  var PICKERS = ['[class$="-thumbs"], [class*="-thumbs "]', '[class$="-loc-tabs"], [class*="-loc-tabs "]'];
  function visibleKids(el) {
    return Array.prototype.filter.call(el.children, function (c) { return c.offsetParent !== null; });
  }
  function thumbsFor(box) {
    for (var p = 0; p < PICKERS.length; p++) {
      var scope = box;
      for (var i = 0; i < 4 && scope; i++) {
        var t = scope.querySelector(PICKERS[p]);
        if (t && visibleKids(t).length > 1) return t;
        scope = scope.parentElement;
      }
    }
    return null;
  }
  document.addEventListener('touchstart', function (e) {
    var box = closest(e.target, '[class$="-preview-box"], [class*="-preview-box "]');
    if (!box || e.touches.length !== 1) { swipe = null; return; }
    swipe = { box: box, x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, { passive: true });
  document.addEventListener('touchend', function (e) {
    if (!swipe || !e.changedTouches.length) { swipe = null; return; }
    var dx = e.changedTouches[0].clientX - swipe.x, dy = e.changedTouches[0].clientY - swipe.y;
    var box = swipe.box; swipe = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    var thumbs = thumbsFor(box);
    if (!thumbs) return;
    var kids = visibleKids(thumbs);
    if (kids.length < 2) return;
    var cur = 0;
    kids.forEach(function (c, i) { if (c.classList.contains('active') || c.classList.contains('is-active')) cur = i; });
    var next = (cur + (dx < 0 ? 1 : -1) + kids.length) % kids.length;
    kids[next].click();
    try { kids[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); } catch (x) {}
  }, { passive: true });

  function markSwipeable() {
    Array.prototype.forEach.call(document.querySelectorAll('[class$="-preview-box"], [class*="-preview-box "]'), function (b) {
      var t = thumbsFor(b);
      b.classList.toggle('li-swipeable', !!t);
    });
  }

  /* Boot + redraws ------------------------------------------------------ */
  function refresh() { tuneAll(document); markSwipeable(); }
  var queued = false;
  function queue() {
    if (queued) return;
    queued = true;
    (window.requestAnimationFrame || setTimeout)(function () { queued = false; refresh(); });
  }
  function boot() {
    refresh();
    var main = document.getElementById('MainContent') || document.body;
    if (window.MutationObserver && main) {
      new MutationObserver(function (list) {
        for (var i = 0; i < list.length; i++) if (list[i].addedNodes.length) { queue(); return; }
      }).observe(main.parentNode || main, { childList: true, subtree: true });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
